import { CurrencyPipe } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { SecurityType } from '@models/shares/security-type';
import { ShareTypeEnum, shareTypeOptions } from '@models/shares/share-type.enum';
import { SelectOption } from "@models/ui-models/select-options";
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  CommonModalWrapperComponent
} from '@shared/components/commons/common-modal-wrapper/common-modal-wrapper.component';
import { AutocompleteComponent } from "@shared/controls/autocomplete/autocomplete.component";
import { InputNumberComponent } from '@shared/controls/input-number/input-number.component';
import { InputComponent } from '@shared/controls/input/input.component';
import { ValidationErrorComponent } from '@shared/controls/validation-error/validation-error.component';
import { Guid } from '@shared/utils/guid.helper';
import { setControlDisabled } from '@shared/utils/set-control-disabled';
import { combineLatest, startWith } from 'rxjs';

interface IShareTypeModalLabels {
  header: string;
  class: string;
  customName: string;
  identifier: string;
}

const shareTypeModalLabels: Record<'shareTypes' | 'unitTypes', IShareTypeModalLabels> = {
  shareTypes: {
    header: 'Add Share Type',
    class: 'Share class',
    customName: 'Share Type Custom Name',
    identifier: 'Share Type'
  },
  unitTypes: {
    header: 'Add Unit Type',
    class: 'Unit class',
    customName: 'Unit Type Custom Name',
    identifier: 'Unit Type'
  }
}

@Component({
  selector: 'app-add-share-type-modal',
  standalone: true,
  imports: [
    CommonModalWrapperComponent,
    InputComponent,
    ReactiveFormsModule,
    InputNumberComponent,
    ValidationErrorComponent,
    AutocompleteComponent
  ],
  templateUrl: 'add-share-type-modal.component.html',
  styleUrl: 'add-share-type-modal.component.scss'
})
export class AddShareTypeModalComponent implements OnInit {
  activeModal = inject(NgbActiveModal);
  currencyPipe = inject(CurrencyPipe);
  destroyRef = inject(DestroyRef);

  @Input() securityTypes: SecurityType[] = [];
  @Input() useShareTypes = true;
  @Input() shareTypeOptions: SelectOption<string>[] = shareTypeOptions;

  readonly shareClassCustomErrors = { invalidFormat: 'Only letters and numbers allowed.', maxlength: 'Max length of Share Class is 5.' };

  labels = shareTypeModalLabels.shareTypes;
  form = new FormGroup({
    class: new FormControl<ShareTypeEnum | string | null>(ShareTypeEnum.ORD, [Validators.required, Validators.maxLength(5)]),
    paidAmount: new FormControl<number | null>(0, [Validators.required]),
    unpaidAmount: new FormControl<number | null>(0, [Validators.required]),
    name: new FormControl<string | null>('', [Validators.required]),
    identifier: new FormControl<string | null>(ShareTypeEnum.ORD, [Validators.required]),
  });

  ngOnInit() {
    setControlDisabled(this.form.controls.identifier);
    this.listenFormChanges();
    this.setExistingSecurityTypeValidator();
    this.addShareClassFormatValidator();

    if (!this.useShareTypes) {
      this.labels = shareTypeModalLabels.unitTypes;
    }
  }

  close(): void {
    this.activeModal.dismiss();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const securityType = this.buildSecurityType();
    this.activeModal.close(securityType);
  }

  listenFormChanges(): void {
    combineLatest(
      this.form.controls.class.valueChanges.pipe(startWith(this.form.value.class)),
      this.form.controls.paidAmount.valueChanges,
      this.form.controls.unpaidAmount.valueChanges.pipe(startWith(this.form.value.unpaidAmount))
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([Class, paidAmount, unpaidAmount]) => {
        if (Class === null || paidAmount === null || unpaidAmount === null) {
          return;
        }

        const nominalPrice = paidAmount! + unpaidAmount!;
        const identifier = `${ Class }@${ SecurityType.getMoneyLabel(nominalPrice, this.currencyPipe) }/${ SecurityType.getMoneyLabel(unpaidAmount!, this.currencyPipe) }`;

        this.form.controls.identifier.patchValue(identifier, { emitViewToModelChange: true });
      });
  }

  setExistingSecurityTypeValidator(): void {
    this.form.setValidators([
      Validators.required,
      () => {
        const securityType = this.securityTypes
          .find((securityType) => securityType.isEqual(this.buildSecurityType()));
        return securityType ? { existingSecurityType: true } : null;
      }
    ]);
  }

  private buildSecurityType(): SecurityType {
    return new SecurityType({
      ...this.form.value as Partial<SecurityType>,
      securityTypeId: Guid.generate(),
      nominalPrice: this.form.value.unpaidAmount! + this.form.value.paidAmount!
    });
  }

  private addShareClassFormatValidator(): void {
    const shareClassFormatValidator = (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value as string | null | undefined) ?? '';
      const regex = /^[a-zA-Z0-9]+$/;

      return regex.test(value)
        ? null
        : { invalidFormat: true };
    };
    this.form.controls.class.addValidators([shareClassFormatValidator]);
  }

  get showValidationError(): boolean {
    return this.form.invalid
      && this.form.touched
      && this.form.controls.paidAmount.touched
      && this.form.controls.unpaidAmount.touched;
  }
}
